import katex from 'katex';

/**
 * Common unicode math symbols mapping to clean LaTeX commands
 */
const UNICODE_TO_LATEX_MAP: Record<string, string> = {
  'α': '\\alpha', 'β': '\\beta', 'γ': '\\gamma', 'δ': '\\delta', 'Δ': '\\Delta',
  'ε': '\\varepsilon', 'ζ': '\\zeta', 'η': '\\eta', 'θ': '\\theta', 'Θ': '\\Theta',
  'ι': '\\iota', 'κ': '\\kappa', 'λ': '\\lambda', 'Λ': '\\Lambda', 'μ': '\\mu',
  'ν': '\\nu', 'ξ': '\\xi', 'π': '\\pi', 'Π': '\\Pi', 'ρ': '\\rho',
  'σ': '\\sigma', 'Σ': '\\Sigma', 'τ': '\\tau', 'υ': '\\upsilon', 'φ': '\\phi',
  'Φ': '\\Phi', 'χ': '\\chi', 'ψ': '\\psi', 'Ψ': '\\Psi', 'ω': '\\omega', 'Ω': '\\Omega',
  '≈': '\\approx', '≠': '\\neq', '≤': '\\le', '≥': '\\ge', '±': '\\pm', '×': '\\times',
  '÷': '\\div', '•': '\\cdot', '→': '\\to', '←': '\\gets', '⇒': '\\implies',
  '∞': '\\infty', '°': '^\\circ', '√': '\\sqrt', '∫': '\\int', '∂': '\\partial',
};

/**
 * Intelligent LaTeX pre-processor and normalizer
 * Strips outer delimiters, cleans spacing/unit artifacts, fixes environment names,
 * and translates raw symbols so KaTeX can render any input instantly.
 */
export function normalizeLatexFormula(rawLatex: string): string {
  if (!rawLatex || !rawLatex.trim()) return '';

  let cleaned = rawLatex.trim();

  // 1. Strip enclosing delimiters: \( ... \), \[ ... \], $$ ... $$, $ ... $
  cleaned = cleaned.replace(/^\\\(|^\\\[|^\$\$|^\$|\\$|\\\)|\\\]|\$\$$/g, '').trim();

  // Also handle cases with whitespace or multiple layers of delimiters
  if ((cleaned.startsWith('\\(') && cleaned.endsWith('\\)')) ||
      (cleaned.startsWith('\\[') && cleaned.endsWith('\\]'))) {
    cleaned = cleaned.slice(2, -2).trim();
  }
  if (cleaned.startsWith('$$') && cleaned.endsWith('$$')) {
    cleaned = cleaned.slice(2, -2).trim();
  }
  if (cleaned.startsWith('$') && cleaned.endsWith('$') && cleaned.length > 1) {
    cleaned = cleaned.slice(1, -1).trim();
  }

  // 2. Strip \begin{equation} and \begin{equation*} wrappers
  cleaned = cleaned.replace(/^\\begin\{equation\*?\}/, '').replace(/\\end\{equation\*?\}$/, '').trim();

  // 3. Convert \begin{align} to \begin{aligned} (KaTeX requires aligned inside math mode)
  cleaned = cleaned.replace(/\\begin\{align\*?\}/g, '\\begin{aligned}');
  cleaned = cleaned.replace(/\\end\{align\*?\}/g, '\\end{aligned}');

  // 4. Fix unit spacing artifacts like \mathrm{~V} -> \text{ V} or \mathrm{\ V}
  cleaned = cleaned.replace(/\\mathrm\{~([^}]+)\}/g, '\\text{ $1}');
  cleaned = cleaned.replace(/\\mathrm\{([^~}]+)\}/g, (_, p1) => {
    // If it contains units like V, mA, mV, k\Omega, use \text
    return `\\text{${p1}}`;
  });

  // 5. Replace tilde ~ outside braces with proper spacing \;
  cleaned = cleaned.replace(/~/g, '\\;');

  // 6. Map raw unicode symbols to valid LaTeX commands
  for (const [unicodeChar, latexCmd] of Object.entries(UNICODE_TO_LATEX_MAP)) {
    if (cleaned.includes(unicodeChar)) {
      cleaned = cleaned.split(unicodeChar).join(` ${latexCmd} `);
    }
  }

  // 7. Normalize double spaces
  cleaned = cleaned.replace(/\s+/g, ' ').trim();

  return cleaned;
}

/**
 * Safely renders LaTeX into a DOM element with KaTeX, applying intelligent auto-correction
 */
export function renderSmartKatex(
  rawLatex: string,
  element: HTMLElement,
  options?: { displayMode?: boolean }
): boolean {
  if (!element) return false;

  const normalized = normalizeLatexFormula(rawLatex) || 'x = 0';
  const displayMode = options?.displayMode !== false;

  try {
    katex.render(normalized, element, {
      displayMode,
      throwOnError: false,
      strict: false,
      trust: true,
      errorColor: '#cc0000',
      output: 'htmlAndMathml',
    });
    return true;
  } catch {
    // Fallback: render text safe representation
    element.innerText = `[Formula: ${rawLatex}]`;
    return false;
  }
}
